import React, { useState, useEffect, useRef } from 'react';
import ensureMapsLoaded from './loadMaps';
import { useAuth } from './AuthContext';
import { flatApi, fileApi, S3_BASE_URL } from './api';

// ─── SVG Icons ───
const IconArrowLeft = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>);
const IconArrowRight = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>);
const IconCheck = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const IconUpload = ({ size = 24 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>);
const IconX = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
const IconMapPin = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>);

// ─── Constants ───
const POST_TYPES = ['FLAT', 'ROOM', 'PG'];
const GENDERS = ['MALE', 'FEMALE', 'ANY'];
const FURNISHING = ['FURNISHED', 'SEMI', 'UNFURNISHED'];
const OCCUPANCY = ['SINGLE', 'SHARED', 'ANY'];
const AMENITIES = ['TV', 'FRIDEG', 'KITCHEN', 'WIFI', 'MACHINE', 'AC', 'POWER_BACKUP', 'COOK', 'PARKING'];

const LABEL_MAP = {
    FLAT: 'Flat', ROOM: 'Room', PG: 'PG', ANY: 'Any',
    MALE: 'Male', FEMALE: 'Female',
    FURNISHED: 'Furnished', SEMI: 'Semi-Furnished', UNFURNISHED: 'Unfurnished',
    SINGLE: 'Single', SHARED: 'Shared',
    TV: '📺 TV', FRIDEG: '🧊 Fridge', KITCHEN: '🍳 Kitchen', WIFI: '📶 Wi-Fi',
    MACHINE: '🧺 Washing Machine', AC: '❄️ AC', POWER_BACKUP: '🔋 Power Backup',
    COOK: '👨‍🍳 Cook', PARKING: '🅿️ Parking',
};

// ─── Chip Selector ───
const ChipSelector = ({ label, options, selected, onSelect, multiple = false }) => (
    <div style={{ marginBottom: '24px' }}>
        <label style={fieldLabel}>{label}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {options.map(opt => {
                const isActive = multiple ? (selected || []).includes(opt) : selected === opt;
                return (
                    <div key={opt} onClick={() => {
                        if (multiple) {
                            const arr = selected || [];
                            onSelect(isActive ? arr.filter(x => x !== opt) : [...arr, opt]);
                        } else {
                            onSelect(isActive ? null : opt);
                        }
                    }}
                        style={{ padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', border: `2px solid ${isActive ? '#3B82F6' : '#DDE3DC'}`, background: isActive ? '#EFF6FF' : 'white', color: isActive ? '#2563EB' : '#6B7280', userSelect: 'none' }}>
                        {LABEL_MAP[opt] || opt}
                    </div>
                );
            })}
        </div>
    </div>
);

const fieldLabel = { display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748B', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: '14px', border: '2px solid #DDE3DC', fontSize: '15px', fontWeight: '600', outline: 'none', transition: 'border-color 0.2s', background: 'white', color: '#1F2937', boxSizing: 'border-box' };

const PostListingPage = ({ onBack, editListing = null }) => {
    const { tokens, refreshAccessToken } = useAuth();
    const isEditMode = !!editListing;

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form state
    const [postType, setPostType] = useState(editListing?.postType || null);
    const [location, setLocation] = useState(editListing?.location || null);
    const [rent, setRent] = useState(editListing?.rent?.toString() || '');
    const [occupancy, setOccupancy] = useState(editListing?.occupany || null);
    const [gender, setGender] = useState(editListing?.gender || null);
    const [furnishing, setFurnishing] = useState(editListing?.furnishing || null);
    const [amenities, setAmenities] = useState(editListing?.amenties || []);
    const [description, setDescription] = useState(editListing?.description || '');
    const [negotiable, setNegotiable] = useState(editListing?.negotiable || false);
    const [hideMobile, setHideMobile] = useState(editListing?.isHideMobileNumber || false);

    // Images
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Flat details
    const [isFlat, setIsFlat] = useState(editListing?.isFlat || editListing?.postType === 'FLAT' || false);
    const [bhk, setBhk] = useState(editListing?.flat?.bhk?.toString() || '');
    const [floor, setFloor] = useState(editListing?.flat?.floor?.toString() || '');
    const [totalFloors, setTotalFloors] = useState(editListing?.flat?.totalFloors?.toString() || '');
    const [areaSqft, setAreaSqft] = useState(editListing?.flat?.areaSqft?.toString() || '');

    // PG details
    const [isPG, setIsPG] = useState(editListing?.isPG || editListing?.postType === 'PG' || false);
    const [singleSharingPrice, setSingleSharingPrice] = useState(editListing?.payingGuest?.singleSharingPrice?.toString() || '');
    const [doubleSharingPrice, setDoubleSharingPrice] = useState(editListing?.payingGuest?.doubleSharingPrice?.toString() || '');
    const [tripleSharingPrice, setTripleSharingPrice] = useState(editListing?.payingGuest?.tripleSharingPrice?.toString() || '');
    const [depositeMonth, setDepositeMonth] = useState(editListing?.payingGuest?.depositeMonth?.toString() || '');
    const [noticePeriodMonth, setNoticePeriodMonth] = useState(editListing?.payingGuest?.noticePeriodMonth?.toString() || '');
    const [electricyUnitPrice, setElectricyUnitPrice] = useState(editListing?.payingGuest?.electricyUnitPrice?.toString() || '');

    // Location search
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const searchRef = useRef(null);
    const searchTimeout = useRef(null);

    useEffect(() => {
        if (isEditMode && editListing?.images?.length > 0) {
            setImages(editListing.images.map(key => ({ key, url: `${S3_BASE_URL}${key}` })));
        }
    }, []);

    useEffect(() => {
        ensureMapsLoaded().catch(() => {});
        const init = () => {
            if (window.google?.maps?.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                const div = document.createElement('div');
                placesService.current = new window.google.maps.places.PlacesService(div);
            }
        };
        if (window.google) init();
        else {
            const interval = setInterval(() => { if (window.google) { init(); clearInterval(interval); } }, 200);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handlePostTypeChange = (type) => {
        setPostType(type);
        setIsFlat(type === 'FLAT');
        setIsPG(type === 'PG');
    };

    const handleSearchLocation = (value) => {
        setSearchQuery(value);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!value.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
        setShowSuggestions(true);
        searchTimeout.current = setTimeout(() => {
            if (!autocompleteService.current) return;
            autocompleteService.current.getPlacePredictions(
                { input: value, componentRestrictions: { country: 'in' } },
                (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) setSuggestions(predictions);
                    else setSuggestions([]);
                }
            );
        }, 400);
    };

    const handleSelectLocation = (suggestion) => {
        const placeId = suggestion.place_id;
        setSearchQuery(suggestion.description);
        setShowSuggestions(false);
        if (!placesService.current) return;
        placesService.current.getDetails({ placeId, fields: ['geometry'] }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                setLocation({
                    name: suggestion.structured_formatting?.main_text || suggestion.description,
                    placeId,
                    latitude: place.geometry.location.lat(),
                    longitude: place.geometry.location.lng(),
                });
            }
        });
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) { setError('Maximum 5 images allowed'); return; }
        setUploading(true);
        setError('');
        try {
            for (const file of files) {
                const { key, url } = await fileApi.uploadFile(file, tokens.access, { watermark: true });
                setImages(prev => [...prev, { key, url }]);
            }
        } catch (err) {
            setError('Failed to upload image: ' + (err.message || ''));
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (!postType) { setError('Please select property type'); return; }
        if (!location) { setError('Please select a location'); return; }
        if (!rent) { setError('Please enter rent amount'); return; }

        setLoading(true);
        setError('');
        try {
            const body = {
                location,
                images: images.map(img => img.key),
                rent: parseInt(rent),
                occupany: occupancy,
                gender,
                amenties: amenities,
                postType,
                isHideMobileNumber: hideMobile,
                description,
                negotiable,
                isFlat,
                furnishing,
            };

            if (isFlat) {
                body.flat = {
                    bhk: parseInt(bhk) || 0,
                    floor: parseInt(floor) || 0,
                    totalFloors: parseInt(totalFloors) || 0,
                    areaSqft: parseInt(areaSqft) || 0,
                };
            }

            if (isPG) {
                body.isPG = true;
                body.payingGuest = {
                    singleSharingPrice: parseInt(singleSharingPrice) || 0,
                    doubleSharingPrice: parseInt(doubleSharingPrice) || 0,
                    tripleSharingPrice: parseInt(tripleSharingPrice) || 0,
                    depositeMonth: parseInt(depositeMonth) || 0,
                    noticePeriodMonth: parseInt(noticePeriodMonth) || 0,
                    electricyUnitPrice: parseInt(electricyUnitPrice) || 0,
                };
            }

            if (isEditMode) {
                body.id = editListing.id;
                await flatApi.updateListing(body, tokens.access);
                setSuccess('Listing updated successfully! ✅');
            } else {
                await flatApi.postListing(body, tokens.access);
                await refreshAccessToken();
                setSuccess('Listing posted successfully! 🎉');
            }
            setTimeout(() => onBack(), 1500);
        } catch (err) {
            setError(err.message || 'Failed to post listing');
        } finally {
            setLoading(false);
        }
    };

    const totalSteps = 3;

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            <style>{`
                .post-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                @media (max-width: 480px) { .post-form-grid { grid-template-columns: 1fr; } }
            `}</style>
            {/* Header */}
            <header style={{ background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)', padding: '100px 20px 0', color: 'white', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative orbs */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1, paddingBottom: '24px' }}>
                    <button onClick={() => step > 1 ? setStep(step - 1) : onBack()} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '14px', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', backdropFilter: 'blur(10px)', transition: 'background 0.2s', flexShrink: 0 }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}>
                        <IconArrowLeft />
                    </button>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0, fontSize: '26px', fontWeight: '800', color: 'white', letterSpacing: '-0.5px' }}>{isEditMode ? 'Edit Listing' : 'Post Listing'}</h2>
                        <div style={{ fontSize: '14px', color: '#D9E6DC', marginTop: '4px', fontWeight: '500' }}>Step {step} of {totalSteps}</div>
                    </div>
                </div>

                {/* Progress bar inside header */}
                <div style={{ height: '4px', background: 'rgba(255,255,255,0.15)' }}>
                    <div style={{ height: '100%', background: 'white', width: `${(step / totalSteps) * 100}%`, transition: 'width 0.3s ease', borderRadius: '0 2px 2px 0' }} />
                </div>
            </header>

            {/* Content */}
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 24px 120px' }}>
                {error && <div style={{ background: '#FEF2F2', border: '2px solid #FCA5A5', color: '#B91C1C', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontWeight: '600', fontSize: '14px' }}>{error}</div>}
                {success && <div style={{ background: '#D1FAE5', border: '2px solid #6EE7B7', color: '#047857', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontWeight: '600', fontSize: '14px' }}>{success}</div>}

                {step === 1 && (
                    <div style={{ animation: 'fadeIn 0.3s ease' }}>
                        <ChipSelector label="Property Type *" options={POST_TYPES} selected={postType} onSelect={handlePostTypeChange} />

                        <div style={{ marginBottom: '24px' }} ref={searchRef}>
                            <label style={fieldLabel}>Location *</label>
                            <input type="text" value={searchQuery} onChange={e => handleSearchLocation(e.target.value)} placeholder="Search location..." style={inputStyle} onFocus={() => { if (searchQuery) setShowSuggestions(true); }} />
                            {showSuggestions && suggestions.length > 0 && (
                                <div style={{ background: 'white', border: '1px solid #DDE3DC', borderRadius: '16px', marginTop: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'relative', zIndex: 50 }}>
                                    {suggestions.map((s, i) => (
                                        <div key={i} onClick={() => handleSelectLocation(s)} style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #E5E9E3', transition: 'background 0.15s' }} onMouseOver={e => e.currentTarget.style.background = '#F9FAFB'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                            <div style={{ color: '#10B981' }}><IconMapPin /></div>
                                            <div>
                                                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1F2937' }}>{s.structured_formatting?.main_text}</div>
                                                <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>{s.structured_formatting?.secondary_text}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {location && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', color: '#10B981', fontSize: '13px', fontWeight: '700' }}>
                                    <IconCheck size={16} /> {location.name}
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={fieldLabel}>Monthly Rent (₹) *</label>
                            <input type="number" value={rent} onChange={e => setRent(e.target.value)} placeholder="e.g. 10000" style={inputStyle} />
                        </div>

                        <ChipSelector label="Occupancy" options={OCCUPANCY} selected={occupancy} onSelect={setOccupancy} />
                        <ChipSelector label="Preferred Gender" options={GENDERS} selected={gender} onSelect={setGender} />
                        <ChipSelector label="Furnishing" options={FURNISHING} selected={furnishing} onSelect={setFurnishing} />
                    </div>
                )}

                {step === 2 && (
                    <div style={{ animation: 'fadeIn 0.3s ease' }}>
                        {isFlat && (
                            <div style={{ background: 'white', borderRadius: '20px', padding: '28px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3' }}>
                                <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: '800', color: '#111827' }}>🏠 Flat Details</h3>
                                <div className="post-form-grid">
                                    <div><label style={fieldLabel}>BHK</label><input type="number" value={bhk} onChange={e => setBhk(e.target.value)} placeholder="2" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Floor</label><input type="number" value={floor} onChange={e => setFloor(e.target.value)} placeholder="3" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Total Floors</label><input type="number" value={totalFloors} onChange={e => setTotalFloors(e.target.value)} placeholder="10" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Area (sqft)</label><input type="number" value={areaSqft} onChange={e => setAreaSqft(e.target.value)} placeholder="800" style={inputStyle} /></div>
                                </div>
                            </div>
                        )}

                        {isPG && (
                            <div style={{ background: 'white', borderRadius: '20px', padding: '28px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3' }}>
                                <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: '800', color: '#111827' }}>🏨 PG Pricing</h3>
                                <div className="post-form-grid">
                                    <div><label style={fieldLabel}>Single (₹)</label><input type="number" value={singleSharingPrice} onChange={e => setSingleSharingPrice(e.target.value)} placeholder="8000" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Double (₹)</label><input type="number" value={doubleSharingPrice} onChange={e => setDoubleSharingPrice(e.target.value)} placeholder="5000" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Triple (₹)</label><input type="number" value={tripleSharingPrice} onChange={e => setTripleSharingPrice(e.target.value)} placeholder="4000" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Deposit (months)</label><input type="number" value={depositeMonth} onChange={e => setDepositeMonth(e.target.value)} placeholder="2" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Notice (months)</label><input type="number" value={noticePeriodMonth} onChange={e => setNoticePeriodMonth(e.target.value)} placeholder="1" style={inputStyle} /></div>
                                    <div><label style={fieldLabel}>Electric /unit (₹)</label><input type="number" value={electricyUnitPrice} onChange={e => setElectricyUnitPrice(e.target.value)} placeholder="10" style={inputStyle} /></div>
                                </div>
                            </div>
                        )}

                        <ChipSelector label="Amenities" options={AMENITIES} selected={amenities} onSelect={setAmenities} multiple />
                    </div>
                )}

                {step === 3 && (
                    <div style={{ animation: 'fadeIn 0.3s ease' }}>
                        {/* Image Upload */}
                        <div style={{ marginBottom: '28px' }}>
                            <label style={fieldLabel}>Photos (max 5)</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px' }}>
                                {images.map((img, idx) => (
                                    <div key={idx} style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', aspectRatio: '1', border: '2px solid #DDE3DC' }}>
                                        <img src={img.url} alt={`Upload ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <button onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(239,68,68,0.9)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                            <IconX size={14} />
                                        </button>
                                    </div>
                                ))}
                                {images.length < 5 && (
                                    <label style={{ aspectRatio: '1', borderRadius: '16px', border: '2px dashed #D1D5DB', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#F8FAFC', transition: 'all 0.2s', gap: '8px', color: '#9CA3AF' }}>
                                        <IconUpload />
                                        <span style={{ fontSize: '12px', fontWeight: '700' }}>{uploading ? 'Uploading...' : 'Add Photo'}</span>
                                        <input type="file" accept="image/*" multiple onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploading} />
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={fieldLabel}>Description</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe your property, nearby facilities, rules, etc." rows={5} style={{ ...inputStyle, resize: 'vertical', minHeight: '120px', fontFamily: 'inherit' }} />
                        </div>

                        {/* Toggles */}
                        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                            <div>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#1F2937' }}>Price Negotiable</div>
                                <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '2px' }}>Allow price discussion</div>
                            </div>
                            <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px' }}>
                                <input type="checkbox" checked={negotiable} onChange={e => setNegotiable(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                                <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: negotiable ? '#3B82F6' : '#D1D5DB', borderRadius: '28px', transition: '0.3s' }} />
                                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', left: negotiable ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: '0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }} />
                            </label>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                            <div>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#1F2937' }}>Hide Phone Number</div>
                                <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '2px' }}>Users will chat instead</div>
                            </div>
                            <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px' }}>
                                <input type="checkbox" checked={hideMobile} onChange={e => setHideMobile(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                                <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: hideMobile ? '#3B82F6' : '#D1D5DB', borderRadius: '28px', transition: '0.3s' }} />
                                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', left: hideMobile ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: '0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }} />
                            </label>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Navigation */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid #DDE3DC', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'center', boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 50 }}>
                <div style={{ maxWidth: '800px', width: '100%', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {step > 1 && (
                        <button onClick={() => setStep(step - 1)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', color: '#6B7280', fontWeight: '700', fontSize: '15px', cursor: 'pointer', padding: '12px 20px' }}>
                            <IconArrowLeft size={18} /> Back
                        </button>
                    )}
                    <div style={{ flex: 1 }}>
                        {step < totalSteps ? (
                            <button onClick={() => setStep(step + 1)} style={{ width: '100%', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '16px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(47, 125, 87, 0.3)' }}>
                                Continue <IconArrowRight size={18} />
                            </button>
                        ) : (
                            <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '16px', fontWeight: '800', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: loading ? 'none' : '0 8px 24px rgba(47, 125, 87, 0.3)' }}>
                                <IconCheck size={18} /> {loading ? 'Saving...' : (isEditMode ? 'Update Listing' : 'Post Listing')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PostListingPage;
